import React, { useState, useEffect } from 'react';
import { X, UserPlus, Mail, Phone, User, Loader2, CheckCircle2, Shield } from 'lucide-react';
import apiClient from '../../Api/ApiClient';

interface CreateStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateStaffModal: React.FC<CreateStaffModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    groupId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoadingGroups(true);
      apiClient.get<any>('/api/admin/staff-groups')
        .then(res => {
          // Filtrer uniquement les groupes de catégorie STAFF
          const staffGroups = (res || []);
          setGroups(staffGroups);
          if (staffGroups.length > 0 && !formData.groupId) {
            setFormData(prev => ({ ...prev, groupId: staffGroups[0].id }));
          }
        })
        .catch(console.error)
        .finally(() => setLoadingGroups(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<any>('/api/admin/staff', formData);
      if (response && response.success) {
        setSuccessMsg(response.message || "Le compte Staff a été créé avec succès.");
        setTimeout(() => {
          setSuccessMsg(null);
          setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '',
    groupId: '' });
          onSuccess();
          onClose();
        }, 3000);
      } else {
        setError(response.message || "Erreur lors de la création.");
      }
    } catch (err: any) {
      setError(err.message || "Erreur de connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-5 right-5 p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-colors z-10">
          <X className="w-5 h-5" />
        </button>

        <div className="bg-gradient-to-r from-primary-900 to-primary-700 p-8 pb-10 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-display font-extrabold">Nouveau Staff</h2>
          <p className="text-primary-100 mt-2 text-sm">Créez un compte pour un membre de votre équipe.</p>
        </div>

        <div className="p-8 -mt-6 bg-white rounded-t-3xl relative">
          {successMsg ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-zinc-900 mb-1">Création réussie !</h3>
                <p className="text-sm text-zinc-500">{successMsg}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl font-medium border border-red-100 text-center">
                  {error}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Prénom *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full pl-9 pr-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" placeholder="Jean" required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Nom *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full pl-9 pr-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" placeholder="Dupont" required />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Adresse Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-9 pr-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" placeholder="jean.dupont@docarn.com" required />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Numéro de Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} className="w-full pl-9 pr-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" placeholder="+237 600 000 000" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Groupe / Rôle</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <select 
                    name="groupId" 
                    value={formData.groupId} 
                    onChange={(e: any) => handleChange(e)} 
                    className="w-full pl-9 pr-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none"
                    disabled={loadingGroups || groups.length === 0}
                  >
                    {loadingGroups ? (
                      <option value="">Chargement des groupes...</option>
                    ) : groups.length === 0 ? (
                      <option value="">Aucun groupe STAFF disponible</option>
                    ) : (
                      groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.groupName}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className="pt-4">
                <button type="submit" disabled={loading} className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-primary-600/30 transition-all flex items-center justify-center gap-2">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Création en cours...</>
                  ) : (
                    <>Créer le compte Staff</>
                  )}
                </button>
                <p className="text-[10px] text-zinc-400 text-center mt-3">Un email contenant le mot de passe sera automatiquement envoyé.</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
